import { API } from '../api.js';
import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLang } from '../contexts/LangContext';
import { useNavigate } from 'react-router-dom';
import styles from './PlansTab.module.css';

export default function PlansTab({ onToast }) {
  const { user, token, refreshUser } = useAuth();
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [plans, setPlans]     = useState([]);
  const [billing, setBilling] = useState('monthly');
  const [loading, setLoading] = useState(true);
  const [changing, setChanging] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/plans`)
      .then(r => r.json())
      .then(data => { setPlans(data); setLoading(false); });
  }, []);

  async function selectPlan(planId) {
    if (!user) { navigate('/login'); return; }
    if (user.plan_id === planId) return;
    setChanging(planId);
    try {
      const res = await fetch(`${API}/api/subscriptions/current`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ plan_id: planId, billing_cycle: billing }),
      });
      if (!res.ok) throw new Error((await res.json()).error);
      await refreshUser();
      onToast(t('plans.upgraded'));
    } catch (err) {
      onToast(err.message);
    }
    setChanging(null);
  }

  const yearly_saving = (p) => p.price_monthly > 0
    ? Math.round((1 - p.price_yearly / (p.price_monthly * 12)) * 100) : 0;

  if (loading) return <div style={{ color: 'var(--text2)', textAlign: 'center', padding: 60 }}>{t('common.loading')}</div>;

  return (
    <div>
      <div className={styles.header}>
        <h2 className={styles.title}>{t('plans.title')}</h2>
        <p className={styles.subtitle}>{t('plans.subtitle')}</p>

        {/* Billing toggle */}
        <div className={styles.billingToggle}>
          <button
            className={`${styles.toggleBtn} ${billing === 'monthly' ? styles.active : ''}`}
            onClick={() => setBilling('monthly')}
          >{t('plans.monthly')}</button>
          <button
            className={`${styles.toggleBtn} ${billing === 'yearly' ? styles.active : ''}`}
            onClick={() => setBilling('yearly')}
          >
            {t('plans.yearly')}
            <span className={styles.saveBadge}>{t('plans.save')} ~17%</span>
          </button>
        </div>
      </div>

      <div className={styles.grid}>
        {plans.map(plan => {
          const isCurrent  = user?.plan_id === plan.id;
          const isEnterprise = plan.price_monthly === 0 && plan.id > 1;
          const price      = billing === 'yearly' ? plan.price_yearly : plan.price_monthly;
          const per        = billing === 'yearly' ? t('plans.perYear') : t('plans.perMonth');
          const name       = lang === 'th' ? plan.name_th : plan.name;
          const features   = lang === 'th' ? plan.features_th : plan.features;
          const saving     = yearly_saving(plan);
          const isHighlight = plan.name === 'Pro';

          return (
            <div
              key={plan.id}
              className={`${styles.planCard} ${isCurrent ? styles.current : ''} ${isHighlight ? styles.highlight : ''}`}
              style={{ '--plan-color': plan.badge_color }}
            >
              {isHighlight && <div className={styles.popularBadge}>⭐ Most Popular</div>}
              {isCurrent   && <div className={styles.currentBadge}>{t('plans.currentPlan')}</div>}

              <div className={styles.planHeader}>
                <div className={styles.planName}>{name}</div>
                <div className={styles.planPrice}>
                  {isEnterprise
                    ? <span className={styles.customPrice}>{t('plans.custom')}</span>
                    : plan.price_monthly === 0
                      ? <span className={styles.freePrice}>{t('plans.free')}</span>
                      : (
                        <>
                          <span className={styles.currency}>฿</span>
                          <span className={styles.amount}>{price.toLocaleString()}</span>
                          <span className={styles.per}>{per}</span>
                        </>
                      )
                  }
                </div>
                {billing === 'yearly' && saving > 0 && (
                  <div className={styles.savingNote}>{t('plans.save')} ฿{(plan.price_monthly * 12 - plan.price_yearly).toLocaleString()}/yr</div>
                )}
              </div>

              <ul className={styles.featureList}>
                {features.map((f, i) => <li key={i} className={styles.featureItem}><span className={styles.check} style={{ color: plan.badge_color }}>✓</span> {f}</li>)}
              </ul>

              <div className={styles.planFooter}>
                {isEnterprise ? (
                  <button className="btn btn-ghost" style={{ width: '100%', justifyContent: 'center' }}>{t('plans.custom')}</button>
                ) : isCurrent ? (
                  <button className="btn" style={{ width: '100%', justifyContent: 'center', background: `${plan.badge_color}22`, color: plan.badge_color, border: `1px solid ${plan.badge_color}44` }} disabled>
                    ✓ {t('plans.currentPlan')}
                  </button>
                ) : (
                  <button
                    className="btn btn-primary"
                    style={{ width: '100%', justifyContent: 'center', background: isHighlight ? plan.badge_color : undefined }}
                    onClick={() => selectPlan(plan.id)}
                    disabled={changing === plan.id}
                  >
                    {changing === plan.id ? t('common.loading') : (user?.plan_id > plan.id ? t('plans.downgrade') : t('plans.upgrade'))}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
